import { useEffect, useState } from "react"
import { gql } from "@apollo/client"
import { useTranslation } from "react-i18next"
import Skeleton from "react-loading-skeleton"
import { useMutation, useQuery } from "@oysterhr/core"
import AddDocumentModal from "~/app/components/AddDocumentModal"
import { DocumentProps } from "~/app/components/AddDocumentModal/types"
import { NavigationButtons } from "~/shared/components/NavigationButtons"
import { SectionTitle } from "~/shared/components/SectionTitle"
import { Document, Employment, Engagement } from "@oysterhr/schema"
import { Icon, Alert, Button, useToast, Toast } from "@oysterhr/ui"
import { useReusableWizard } from "@oysterhr/feature-components"
import { usePearlContext } from "@oysterhr/pearl"
import type { HiringFTEPearlContext } from "~/shared/components/PearlTooltip/types"
import { useForm } from "react-hook-form"
import { handleFormErrors } from "~/shared/utils/formHelpers"
import { UniversalErrorSummary } from "~/app/components/UniversalErrorSummary"
import { PearlTooltip } from "~/shared/components/PearlTooltip"

export type DocumentsPearlFields = "documents"

const HIRING_FTE_COMPANY_CREATE_DOCUMENT_HIRING_FLOW = gql`
  mutation HiringFTECompanyCreateDocumentHiringFlow(
    $entityId: ID!
    $signedDocumentId: String!
    $title: String!
    $description: String
  ) {
    createDocument(
      input: {
        entityId: $entityId
        signedDocumentId: $signedDocumentId
        title: $title
        description: $description
      }
    ) {
      errors
    }
  }
`

const HIRING_FTE_COMPANY_GET_ENGAGEMENT_RELATED_DOCUMENTS_HIRING_FLOW = gql`
  query HiringFTECompanyGetEngagementRelatedDocumentsHiringFlow($id: ID!) {
    engagement(engagementId: $id) {
      id
      documents {
        id
        filename
        title
      }
    }
  }
`
const HIRING_FTE_COMPANY_DESTROY_DOCUMENT_HIRING_FLOW = gql`
  mutation HiringFTECompanyDestroyDocumentHiringFlow($documentId: String!) {
    destroyDocument(input: { documentId: $documentId }) {
      errors
    }
  }
`

type DocumentRowProps = {
  document: Document
}

const DocumentRow = ({ document }: DocumentRowProps) => {
  const [destroyDocumentMutation] = useMutation(HIRING_FTE_COMPANY_DESTROY_DOCUMENT_HIRING_FLOW)

  const destroyDocument = async (id: string) => {
    await destroyDocumentMutation({
      variables: { documentId: id },
      refetchQueries: [
        "HiringFTECompanyGetCompanyEmploymentEngagement",
        HIRING_FTE_COMPANY_GET_ENGAGEMENT_RELATED_DOCUMENTS_HIRING_FLOW,
      ],
    })
  }

  return (
    <div className="grid w-full grid-cols-11 gap-x-2 p-2 hover:bg-gray-200">
      <div className="col-span-5 break-words">{document.title}</div>
      <div className="col-span-5 break-words">{document.filename}</div>
      <div className="col-start-11">
        <button className="absolute right-2" onClick={() => destroyDocument(document.id)}>
          <Icon data-testid="Delete" name="Trash" size="sm" />
        </button>
      </div>
    </div>
  )
}

type DocumentsProps = {
  employment: Employment
  setIsLastStep: (isLastStep: boolean) => void
  showConfirmationModal: () => void
  isLastStep: boolean
}

export function Documents({
  employment,
  setIsLastStep,
  showConfirmationModal,
  isLastStep,
}: DocumentsProps) {
  const { setFocusedField } = usePearlContext<HiringFTEPearlContext>()
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslation("company_interface/CompanyNewHireFTEPage")
  const { goToNext } = useReusableWizard()
  const toast = useToast()

  // Needed to coherently render errors in `UniversalErrorSummary`
  const {
    setError,
    formState: { errors },
    clearErrors,
  } = useForm()

  useEffect(() => {
    setIsLastStep(isLastStep)

    return () => {
      setIsLastStep(false)
    }
  }, [isLastStep, setIsLastStep])

  useEffect(() => {
    setFocusedField("documents")
  }, [setFocusedField])

  const { data, loading: fetchingDocuments } = useQuery<{ engagement: Engagement }>(
    HIRING_FTE_COMPANY_GET_ENGAGEMENT_RELATED_DOCUMENTS_HIRING_FLOW,
    {
      variables: { id: employment.id },
      fetchPolicy: "network-only",
    },
  )
  const documents = data?.engagement?.documents || []

  const [uploadDocumentMutation, { loading: uploadingDocument }] = useMutation(
    HIRING_FTE_COMPANY_CREATE_DOCUMENT_HIRING_FLOW,
    {
      setPayloadErrorsHook: errors => handleFormErrors({ errors }, setError),
      onCompleted: () => clearErrors(),
    },
  )

  const addDocument = async (values: DocumentProps) => {
    const variables = {
      signedDocumentId: values.signedDocumentId,
      title: values.title,
      entityId: employment.id,
      description: values.description,
    }

    await uploadDocumentMutation({
      variables,
      refetchQueries: [HIRING_FTE_COMPANY_GET_ENGAGEMENT_RELATED_DOCUMENTS_HIRING_FLOW],
    })

    setShowModal(false)
  }

  const onSubmit = () => {
    if (Object.keys(errors).length > 0) return

    if (isLastStep) {
      showConfirmationModal()
    } else {
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>{t("flashMessages.success")}</Toast.Content>
          </Toast>
        ),
      })
      goToNext()
    }
  }

  if (fetchingDocuments) {
    return (
      <div className="space-y-4">
        <div className="my-4">
          <Skeleton className="max-w-48 text-xl" />
          <Skeleton className="h-28" />
        </div>
        <NavigationButtons
          loading={false}
          disabled
          isLast={isLastStep}
          analyticsBase="new-hire-form"
        />
      </div>
    )
  }

  return (
    <div className="relative mt-12 justify-between" data-testid="documents-step">
      <SectionTitle
        title={t("documents.title")}
        testId="upload-files-header-title"
        endAdornment={<PearlTooltip fieldName="documents" />}
      />
      {documents.map(document => (
        <DocumentRow key={document.id} document={document} />
      ))}
      <div className="mt-8 grid w-full grid-cols-3 items-center">
        <div className="col-span-2 mr-2">
          <span className="ml-1 text-base font-medium leading-6 text-black">
            {t(`documents.${documents.length ? "subtitleMore" : "subtitle"}`)}
          </span>
          <span className="font-regular text-gray-300">{t("documents.optional")}</span>
        </div>
        <div className="col-span-1 col-start-3">
          <Button
            className="right-0"
            variation="secondary"
            startAdornment={<Icon name="PlusCircle" size="sm" />}
            onClick={() => setShowModal(true)}
          >
            {t("documents.cta")}
          </Button>
        </div>
      </div>
      <Alert className="mt-4" variation="info">
        <Alert.Icon />
        <Alert.Content>{t("inlineAlerts.documents")}</Alert.Content>
      </Alert>
      {/* Error Summary */}
      <UniversalErrorSummary
        className="mt-8"
        description={t("errorSummary.description", {
          ns: "company_interface/CompanyNewHireFTEPage",
        })}
        title={t("errorSummary.title", { ns: "company_interface/CompanyNewHireFTEPage" })}
        errors={errors}
      />
      <NavigationButtons
        loading={uploadingDocument}
        onNext={onSubmit}
        isLast={isLastStep}
        analyticsBase="new-hire-form"
      />
      {showModal && <AddDocumentModal onSubmit={addDocument} setShowModal={setShowModal} />}
    </div>
  )
}
